import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, User, Star, ChevronDown, BarChart3, MoreHorizontal } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const { user, isAuthenticated, logout } = useAuth();

  const mainLinks = [
    { to: "/", label: "Hem" },
    { to: "/tabell", label: "Maratontabell" },
    { to: "/matcher", label: "Matcher" },
    { to: "/live", label: "Live" },
    { to: "/ai-analys", label: "Match-analys" },
  ];

  const statsLinks = [
    { to: "/skytteliga", label: "Skytteliga" },
    { to: "/assistliga", label: "Assistliga" },
    { to: "/kortliga", label: "Kortliga" },
    { to: "/lan/stockholm", label: "Länsrankning" },
  ];

  const moreLinks = [
    { to: "/supporterindex", label: "Supporterindex" },
    { to: "/jamfor", label: "Jämför lag" },
    { to: "/nyheter", label: "Nyheter" },
    { to: "/blogg", label: "Blogg" },
    { to: "/om", label: "Om" },
  ];

  const allLinks = [...mainLinks, ...statsLinks, ...moreLinks];

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const isGroupActive = (links) => links.some(l => isActive(l.to));

  const handleNavClick = (e, to) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: "instant" });
    navigate(to);
    setMenuOpen(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const NavLink = ({ to, label, className = "" }) => (
    <a
      href={to}
      onClick={(e) => handleNavClick(e, to)}
      data-testid={`nav-link-${to.replace("/", "") || "home"}`}
      className={`text-sm font-medium tracking-wide transition-colors cursor-pointer whitespace-nowrap ${
        isActive(to) ? "text-[#FECC00]" : "text-slate-300 hover:text-white"
      } ${className}`}
    >
      {label}
    </a>
  );

  return (
    <nav className="bg-[#0F172A] text-white sticky top-0 z-50" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-14">
          {/* Logo */}
          <a
            href="/"
            onClick={(e) => handleNavClick(e, "/")}
            className="flex items-center group cursor-pointer shrink-0"
            data-testid="navbar-logo"
          >
            <picture>
              <source srcSet="/logo.webp" type="image/webp" />
              <img
                src="/logo.png"
                alt="Fotbollsliv"
                className="h-10 w-auto object-contain group-hover:scale-105 transition-transform"
                width="120"
                height="40"
                loading="eager"
              />
            </picture>
          </a>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-1">
            {/* Main links */}
            {mainLinks.map((link) => (
              <NavLink key={link.to} to={link.to} label={link.label} className="px-2.5 py-1.5" />
            ))}

            {/* Statistik dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className={`flex items-center gap-1 text-sm font-medium px-2.5 py-1.5 rounded transition-colors whitespace-nowrap ${
                    isGroupActive(statsLinks) ? "text-[#FECC00]" : "text-slate-300 hover:text-white"
                  }`}
                  data-testid="nav-stats-dropdown"
                >
                  <BarChart3 className="w-3.5 h-3.5" />
                  Statistik
                  <ChevronDown className="w-3 h-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-44">
                {statsLinks.map((link) => (
                  <DropdownMenuItem
                    key={link.to}
                    onClick={() => { navigate(link.to); window.scrollTo(0, 0); }}
                    className={`cursor-pointer ${isActive(link.to) ? "text-yellow-600 font-semibold" : ""}`}
                    data-testid={`nav-link-${link.to.replace("/", "")}`}
                  >
                    {link.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mer dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className={`flex items-center gap-1 text-sm font-medium px-2.5 py-1.5 rounded transition-colors whitespace-nowrap ${
                    isGroupActive(moreLinks) ? "text-[#FECC00]" : "text-slate-300 hover:text-white"
                  }`}
                  data-testid="nav-more-dropdown"
                >
                  Mer
                  <ChevronDown className="w-3 h-3" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-44">
                {moreLinks.map((link) => (
                  <DropdownMenuItem
                    key={link.to}
                    onClick={() => { navigate(link.to); window.scrollTo(0, 0); }}
                    className={`cursor-pointer ${isActive(link.to) ? "text-yellow-600 font-semibold" : ""}`}
                    data-testid={`nav-link-${link.to.replace("/", "")}`}
                  >
                    {link.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Auth */}
            <div className="ml-2 pl-2 border-l border-slate-700 flex items-center gap-2">
              {isAuthenticated ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 gap-1.5 text-slate-300 hover:text-white hover:bg-slate-700"
                      data-testid="user-menu-btn"
                    >
                      {user?.picture ? (
                        <img src={user.picture} alt="" className="w-5 h-5 rounded-full" />
                      ) : (
                        <User className="w-4 h-4" />
                      )}
                      <span className="max-w-[80px] truncate text-xs">{user?.name?.split(" ")[0]}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-44">
                    <DropdownMenuItem onClick={() => navigate("/profil")} className="cursor-pointer">
                      <User className="w-4 h-4 mr-2" /> Min profil
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600">
                      Logga ut
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <>
                  <button
                    onClick={() => navigate("/logga-in")}
                    className="text-xs text-slate-400 hover:text-white transition-colors whitespace-nowrap"
                    data-testid="login-link"
                  >
                    Logga in
                  </button>
                  <Button
                    size="sm"
                    className="h-8 text-xs bg-[#FECC00] hover:bg-yellow-400 text-[#0F172A] font-semibold"
                    onClick={() => navigate("/registrera")}
                    data-testid="register-cta-btn"
                  >
                    <Star className="w-3.5 h-3.5 mr-1" />
                    Följ ditt lag
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <button
            className="lg:hidden p-1.5 rounded hover:bg-slate-700 transition-colors"
            onClick={() => setMenuOpen(!menuOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Meny"
          >
            {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="lg:hidden pb-4 border-t border-slate-700 pt-3 max-h-[calc(100vh-56px)] overflow-y-auto">
            <div className="grid grid-cols-2 gap-1">
              {allLinks.map((link) => (
                <a
                  key={link.to}
                  href={link.to}
                  className={`py-2.5 px-3 text-sm font-medium rounded transition-colors cursor-pointer ${
                    isActive(link.to)
                      ? "text-[#FECC00] bg-slate-800"
                      : "text-slate-300 hover:bg-slate-800"
                  }`}
                  onClick={(e) => handleNavClick(e, link.to)}
                  data-testid={`mobile-nav-${link.to.replace("/", "") || "home"}`}
                >
                  {link.label}
                </a>
              ))}
            </div>

            {/* Mobile auth */}
            <div className="pt-3 border-t border-slate-700 mt-3">
              {isAuthenticated ? (
                <div className="flex items-center justify-between px-3">
                  <a
                    href="/profil"
                    onClick={(e) => handleNavClick(e, "/profil")}
                    className="text-sm font-medium text-slate-300 flex items-center gap-2"
                  >
                    <User className="w-4 h-4" /> Min profil
                  </a>
                  <button
                    onClick={handleLogout}
                    className="text-sm font-medium text-red-400"
                  >
                    Logga ut
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-3 px-3">
                  <Button
                    size="sm"
                    className="flex-1 h-9 bg-[#FECC00] hover:bg-yellow-400 text-[#0F172A] font-semibold text-sm"
                    onClick={() => { navigate("/registrera"); setMenuOpen(false); }}
                    data-testid="mobile-register-cta"
                  >
                    <Star className="w-4 h-4 mr-1" />
                    Följ ditt lag
                  </Button>
                  <a
                    href="/logga-in"
                    onClick={(e) => handleNavClick(e, "/logga-in")}
                    className="text-sm text-slate-400 whitespace-nowrap"
                  >
                    Logga in
                  </a>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
